import { useEffect } from 'react';
import useLabelingStore from '../store/useDetectionStore';
import useDetectionRightStore from '../store/componentStores/useDetectionRightStore';
import { useSwal } from '../../../../../components/Notifications/swal/SwalCom';
import { createBoxObject, createPolygonObject } from '../utils/loadImageToCanvas';
import {
  PASTE_OFFSET_PX,
  offsetAnnotation,
  offsetPolygon,
  readClipboardEntry,
} from '../utils/annotationClipboard';

const useKeyboardShortcuts = (saveState, undo, redo) => {
  const {
    canvas, zoomLevel, copiedBoxes, getCurrentImage, updateAnnotations, updatePolygons,
    setCopiedBoxes, setSelectedAnnIndex, BASE_STROKE_WIDTH, nextImage, prevImage, classes,
    setMode, startPolygon, isDrawingPolygon,
    showLabels, setShowLabels, showFill, setShowFill, isCrosshairEnabled, setCrosshairEnabled,
    zoomIn, zoomOut, zoomToFit, zoomToActual,
  } = useLabelingStore();
  const { isModalOpen } = useDetectionRightStore();
  const { swalFail } = useSwal();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!canvas) return;

      // Undo: Ctrl+Z (or Cmd+Z on Mac) / Redo: Ctrl+Shift+Z or Ctrl+Y
      if (!isTextInput(e.target) && (e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y')) {
        if (e.key === 'z') {
          if (e.shiftKey) {
            e.preventDefault();
            redo?.();
          } else {
            e.preventDefault();
            undo?.();
          }
        } else if (e.key === 'y') {
          e.preventDefault();
          redo?.();
        }
        return;
      }

      if (e.key === 'Delete') {
        const active = canvas.getActiveObject();
        if (!active) return;

        saveState?.();

        const currentImage = getCurrentImage();

        // Case 1: multiple selection
        if (active.type === 'activeSelection') {
          const objects = active.getObjects();
          objects.forEach((obj) => {
            const idx = obj.data?.annIndex ?? obj.data?.polyIndex;
            if (idx === undefined) return;

            if (obj.data.type === 'bbox') {
              updateAnnotations(currentImage.annotations.filter((_, i) => i !== idx));

              canvas.getObjects('rect').forEach((r) => {
                if (r.data.annIndex > idx) {
                  r.data.annIndex -= 1;
                }
              });
            } else if (obj.data.type === 'polygon') {
              updatePolygons(currentImage.polygons.filter((_, i) => i !== idx));

              canvas.getObjects('polygon').forEach((p) => {
                if (p.data.polyIndex > idx) {
                  p.data.polyIndex -= 1;
                }
              });
            }

            canvas.remove(obj);
          });
        }
        // Case 2: single object
        else {
          const idx = active.data?.annIndex ?? active.data?.polyIndex;
          if (idx === undefined) return;

          if (active.data.type === 'bbox') {
            updateAnnotations(currentImage.annotations.filter((_, i) => i !== idx));

            canvas.getObjects('rect').forEach((r) => {
              if (r.data.annIndex > idx) {
                r.data.annIndex -= 1;
              }
            });
          } else if (active.data.type === 'polygon') {
            updatePolygons(currentImage.polygons.filter((_, i) => i !== idx));

            canvas.getObjects('polygon').forEach((p) => {
              if (p.data.polyIndex > idx) {
                p.data.polyIndex -= 1;
              }
            });
          }

          canvas.remove(active);
        }

        setSelectedAnnIndex(-1);
        canvas.discardActiveObject(); // clear selection after delete
        canvas.requestRenderAll();
      }
      else if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c') {
          const actives = canvas.getActiveObjects();
          if (actives.length !== 1) {
            setCopiedBoxes([]);
            if (actives.length > 1) {
              swalFail('Copy blocked', 'You cannot copy multiple objects. Please select only one.');
            }
            return;
          }

          // the stored record, not the fabric object — see annotationClipboard
          const entry = readClipboardEntry(actives[0], getCurrentImage());
          setCopiedBoxes(entry ? [entry] : []);
        } else if (e.key === 'v' && copiedBoxes.length === 1) {
          const currentImage = getCurrentImage();
          const imageWidth = Number(currentImage?.originalWidth) || 0;
          const imageHeight = Number(currentImage?.originalHeight) || 0;
          if (!imageWidth || !imageHeight) return;

          saveState?.();

          // a constant nudge on screen, whatever the zoom and the image size
          const offsetX = PASTE_OFFSET_PX / (zoomLevel || 1) / imageWidth;
          const offsetY = PASTE_OFFSET_PX / (zoomLevel || 1) / imageHeight;

          const drawContext = {
            width: imageWidth,
            height: imageHeight,
            classes,
            zoom: zoomLevel || 1,
            baseStroke: BASE_STROKE_WIDTH,
            showFill,
          };

          const entry = copiedBoxes[0];
          let pasted = null;

          if (entry.type === 'bbox') {
            const ann = offsetAnnotation(entry.ann, offsetX, offsetY);
            const newAnns = [...currentImage.annotations, ann];
            updateAnnotations(newAnns);
            pasted = createBoxObject(ann, newAnns.length - 1, drawContext);
            // re-arm the clipboard with the copy just made, so pasting again
            // cascades instead of stacking every copy on the same spot
            setCopiedBoxes([{ type: 'bbox', ann }]);
          } else if (entry.type === 'polygon') {
            const poly = offsetPolygon(entry.poly, offsetX, offsetY);
            const newPolygons = [...currentImage.polygons, poly];
            updatePolygons(newPolygons);
            pasted = createPolygonObject(poly, newPolygons.length - 1, drawContext);
            setCopiedBoxes([{ type: 'polygon', poly }]);
          }

          if (pasted) {
            canvas.add(pasted);
            canvas.setActiveObject(pasted);
          }
          canvas.requestRenderAll();
        } else if (e.key === 'v' && copiedBoxes.length > 1) {
          swalFail('Paste blocked', 'You cannot paste multiple objects at once. Please copy only one.');
          setCopiedBoxes([]);
        }
      }

      // ----- single-key tool / view / zoom shortcuts -----
      // (no modifier, not while typing — the clipboard and undo branches above
      //  have already returned for their own combinations)
      if (!isTextInput(e.target) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const key = e.key.toLowerCase();
        const shortcuts = {
          v: () => setMode('select'),
          b: () => setMode('draw'),
          g: () => { startPolygon(); setMode('polygon'); },
          h: () => setMode('pan'),
          l: () => setShowLabels(!showLabels),
          f: () => setShowFill(!showFill),
          c: () => setCrosshairEnabled(!isCrosshairEnabled),
          '0': () => zoomToFit(),
          '1': () => zoomToActual(),
          '+': () => zoomIn(),
          '=': () => zoomIn(),
          '-': () => zoomOut(),
          _: () => zoomOut(),
        };

        if (shortcuts[key]) {
          e.preventDefault();
          shortcuts[key]();
          return;
        }

        if (e.key === 'Escape' && !isDrawingPolygon) {
          canvas.discardActiveObject();
          canvas.requestRenderAll();
          setSelectedAnnIndex(-1);
          return;
        }
      }

      if (e.key === "ArrowRight" || e.key.toLowerCase() === "d") {
        // Check if the event didn't come from a text input or textarea
        if (!isTextInput(e.target)) {
          e.preventDefault(); // stop scrolling or other defaults
          nextImage();
        }
      }

      if (e.key === "ArrowLeft" || e.key.toLowerCase() === "a") {
        // Check if the event didn't come from a text input or textarea
        if (!isTextInput(e.target)) {
          e.preventDefault(); // stop scrolling or other defaults
          prevImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvas, zoomLevel, copiedBoxes, getCurrentImage, updateAnnotations, updatePolygons,
    setCopiedBoxes, setSelectedAnnIndex, BASE_STROKE_WIDTH, swalFail, saveState, undo, redo,
    classes, nextImage, prevImage, setMode, startPolygon, isDrawingPolygon,
    showLabels, setShowLabels, showFill, setShowFill, isCrosshairEnabled, setCrosshairEnabled,
    zoomIn, zoomOut, zoomToFit, zoomToActual]);
};
// Helper function to check if element is a text input
function isTextInput(element) {
  const tagName = element.tagName.toLowerCase();
  const type = element.type ? element.type.toLowerCase() : '';

  // Check for text inputs, textareas, and contenteditable elements
  return (
    tagName === 'textarea' ||
    tagName === 'input' && (
      type === 'text' ||
      type === 'email' ||
      type === 'password' ||
      type === 'search' ||
      type === 'tel' ||
      type === 'url' ||
      type === 'number'
    ) ||
    element.isContentEditable
  );
}
export default useKeyboardShortcuts;